import React from 'react';
import type { CSSProperties, Key, ReactNode } from 'react';
import useStyles from '../../theme/useStyles';

/**
 * One column. `T` is the row type, so `render` receives a fully typed row —
 * referencing a field that does not exist on T is a compile error instead of
 * a silently blank cell.
 */
export interface Column<T> {
  /** React key for the column, and the row property read when `render` is omitted. */
  key: string;
  header: ReactNode;
  render?: (row: T, index: number) => ReactNode;
  width?: string;
  align?: 'left' | 'center' | 'right';
  /** Prevent the cell from wrapping. */
  nowrap?: boolean;
  /**
   * Let a long free-text cell (a remark, a reason, a note) wrap to several lines
   * and break inside a long word, capped at `maxWidth` (default 240px), instead
   * of stretching the row into one endless line the way an uncapped text column
   * does inside this horizontally-scrolling table.
   */
  wrap?: boolean;
  /** Cap for a wrapping column. Defaults to 240px when `wrap` is set. */
  maxWidth?: string;
}

/** A row key: a property name, or a function deriving one. */
export type RowKey<T> = string | ((row: T, index: number) => Key);

export interface DataTableProps<T> {
  columns: Array<Column<T>>;
  rows?: T[];
  rowKey?: RowKey<T>;
  loading?: boolean;
  emptyMessage?: ReactNode;
  onRowClick?: (row: T) => void;
  /** Per-row style, e.g. to highlight selected rows. */
  rowStyle?: (row: T) => CSSProperties | null | undefined;
  minWidth?: string;
  isDarkMode?: boolean;
}

//   <DataTable<ChurchEvent>
//     columns={[
//       { key: 'eventId', header: 'Event ID', render: r => <strong>{r.eventId}</strong> },
//       { key: 'eventName', header: 'Event Name' },
//       { key: 'actions', header: 'Actions', render: r => <Button ... /> }
//     ]}
//     rows={currentItems}
//     emptyMessage="No events scheduled."
//   />
const DataTable = <T,>({
  columns = [],
  rows = [],
  rowKey = 'id',
  loading = false,
  emptyMessage = 'No records found.',
  onRowClick,
  rowStyle,
  minWidth = '800px',
  isDarkMode
}: DataTableProps<T>) => {
  const s = useStyles(isDarkMode);

  // When a column wraps (a notes/remark cell can be several lines tall), the
  // other cells in that row would sit vertically centred against it and read as
  // misaligned. Top-align the body cells so every column lines up on the first
  // line of the row (CHM-014).
  const hasWrap = columns.some((col) => col.wrap);

  const keyFor = (row: T, idx: number): Key =>
    typeof rowKey === 'function'
      ? rowKey(row, idx)
      : ((row as Record<string, unknown>)?.[rowKey] as Key) ?? idx;

  const cellValue = (col: Column<T>, row: T, idx: number): ReactNode =>
    typeof col.render === 'function'
      ? col.render(row, idx)
      : ((row as Record<string, unknown>)?.[col.key] as ReactNode) ?? '-';

  return (
    // `data-table-scroll` is the hook the print stylesheet uses to undo the
    // horizontal scroll: on screen a wide table scrolls inside this box, but on
    // paper the box must let the table shrink to the page instead of clipping
    // the right-hand columns (there is no scrollbar on print).
    <div className="data-table-scroll" style={s.tableContainer}>
      <table style={{ ...s.table, minWidth }}>
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                style={{ ...s.th, ...(col.width ? { width: col.width } : {}), ...(col.align ? { textAlign: col.align } : {}) }}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={columns.length} style={s.emptyCell}>Loading…</td></tr>
          ) : rows.length === 0 ? (
            <tr><td colSpan={columns.length} style={s.emptyCell}>{emptyMessage}</td></tr>
          ) : (
            rows.map((row, idx) => {
              const custom = rowStyle ? rowStyle(row) : null;
              const resting = custom?.backgroundColor || 'transparent';
              return (
                <tr
                  key={keyFor(row, idx)}
                  style={{ ...s.tr, ...(onRowClick ? { cursor: 'pointer' } : {}), ...custom }}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = s.t.bgHover; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = resting; }}
                >
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      style={{
                        ...s.td,
                        ...(hasWrap ? { verticalAlign: 'top' } : {}),
                        ...(col.align ? { textAlign: col.align } : {}),
                        // Right-aligned columns in this app are always amounts
                        // or counts. Equal-width digits let a reader compare
                        // them down the column without re-reading each row.
                        ...(col.align === 'right' ? { fontVariantNumeric: 'tabular-nums' } : {}),
                        ...(col.nowrap ? { whiteSpace: 'nowrap' } : {}),
                        ...(col.wrap
                          ? {
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            maxWidth: col.maxWidth || '240px'
                          }
                          : {})
                      }}
                    >
                      {cellValue(col, row, idx)}
                    </td>
                  ))}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

export default DataTable;
